import type {
  IntentGroup,
  Policy,
  Scenario,
  ConditionGroup,
  SubPolicy,
  PolicyGroup,
} from '../Types/api';
import type { CategoryItem } from '../OfficialScenario/Components/CategoryFilter';
import type { ScenarioGroup } from '../OfficialScenario/Components/ScenarioGroupList';
import type { BranchCardData } from '../OfficialScenario/Components/ScenarioDetail/BranchCard';
import type { TriggerCondition } from '../OfficialScenario/Components/ScenarioDetail/TriggerConditionList';

export const ScenarioSearchField = {
  HEAT_VALUE: 'heatValue',
  UPDATE_TIME: 'updateTime',
} as const;

export const ScenarioSortField = {
  HEAT_VALUE: 'heatValue',
  UPDATE_TIME: 'updateTime',
} as const;

type ScenarioSortFieldValue = (typeof ScenarioSortField)[keyof typeof ScenarioSortField];

// ─── 分类标签映射 ───

export function mapCategories(groups: IntentGroup[], t: (key: string) => string): CategoryItem[] {
  const allCount = groups.reduce((sum, g) => sum + g.scenario.length, 0);
  const items: CategoryItem[] = groups.map((g) => ({
    key: g.intentGroupId,
    label: g.name,
    count: g.scenario.length,
  }));
  return [
    { key: 'all', label: t('scene.aiAgentConfig.filter.option.all'), count: allCount },
    ...items,
  ];
}

// ─── 场景列表映射 ───

export function mapScenarioGroups(groups: IntentGroup[]): ScenarioGroup[] {
  return groups.flatMap((g) =>
    g.scenario.map((s) => ({
      id: s.intentId,
      category: s.groupName,
      name: s.name,
      enabledCount: Number(s.enablePolicyCount),
      totalCount: Number(s.policyCount),
      hotCount: Number(s.heatValue ?? 0),
      updateTime: s.updateTime,
      enabled: s.enable,
    })),
  );
}

// ─── 触发条件映射 ───

function mapConditionGroups(
  conditionGroups?: ConditionGroup[],
  logicOperator?: 'OR' | 'AND',
): TriggerCondition[] {
  if (!conditionGroups?.length) return [];

  const result: TriggerCondition[] = [];
  conditionGroups.forEach((group, groupIdx) => {
    group.conditions.forEach((cond, condIdx) => {
      const isLastInGroup = condIdx === group.conditions.length - 1;
      const isLastGroup = groupIdx === conditionGroups.length - 1;
      result.push({
        label: cond.parameter,
        operator: !isLastInGroup
          ? 'and'
          : !isLastGroup
          ? (logicOperator?.toLowerCase() as 'and' | 'or')
          : undefined,
        groupIndex: groupIdx,
        groupOperator: groupIdx > 0 ? (logicOperator?.toLowerCase() as 'and' | 'or') : undefined,
        conditionOperator: cond.operator,
        expectList: cond.expectList,
      });
    });
  });
  return result;
}

// ─── 分支卡片映射 ───

export interface BranchCardStoreScope {
  allStore: boolean;
  storeInfo?: PolicyGroup['storeInfo'];
}

interface BranchCardDisplayBase {
  title?: string;
  enabled?: boolean;
  triggerConditionSource?: {
    logicOperator?: 'OR' | 'AND';
    conditionGroups?: ConditionGroup[];
  };
  headerVariant?: 'default' | 'sub';
  policyId?: string;
}

type ResolveTodoTypeLabel = (todoType?: string) => string | undefined;

function createStoreScope(policyGroup: PolicyGroup): BranchCardStoreScope {
  return {
    allStore: policyGroup.allStore,
    storeInfo: policyGroup.storeInfo,
  };
}

function resolveReplyLogicDesc(policy: Policy, node: Policy | SubPolicy) {
  const nodeReplyLogicDesc = node.replyLogicDesc?.trim();
  const policyReplyLogicDesc = policy.replyLogicDesc?.trim();
  const isSingleSubPolicy = 'subPolicyId' in node && (policy.subPolicy?.length ?? 0) === 1;

  if (nodeReplyLogicDesc) {
    return nodeReplyLogicDesc;
  }

  if (isSingleSubPolicy && policyReplyLogicDesc) {
    return policyReplyLogicDesc;
  }

  return policyReplyLogicDesc || '';
}

function resolveReplyExample(policy: Policy, node: Policy | SubPolicy) {
  const nodeReplyExample = node.replyExample?.trim();
  const policyReplyExample = policy.replyExample?.trim();
  const isSingleSubPolicy = 'subPolicyId' in node && (policy.subPolicy?.length ?? 0) === 1;

  if (nodeReplyExample) {
    return nodeReplyExample;
  }

  if (isSingleSubPolicy && policyReplyExample) {
    return policyReplyExample;
  }

  return policyReplyExample || '';
}

function resolveReplyMode(policy: Policy, node: Policy | SubPolicy) {
  const nodeReplyMode = node.replyMode;
  const policyReplyMode = policy.replyMode;
  const isSingleSubPolicy = 'subPolicyId' in node && (policy.subPolicy?.length ?? 0) === 1;

  if (nodeReplyMode && nodeReplyMode !== 'RM_UNKNOWN') {
    return nodeReplyMode;
  }

  if (isSingleSubPolicy && policyReplyMode && policyReplyMode !== 'RM_UNKNOWN') {
    return policyReplyMode;
  }

  return nodeReplyMode ?? policyReplyMode;
}

function extractTextReplyContent(node: Policy | SubPolicy) {
  const textList =
    node.replyContent?.flatMap((item) => {
      const isTextType = !item.type || item.type === 'TEXT';
      if (!isTextType) {
        return [];
      }

      const textContents = item.textContents;
      const textItems = Array.isArray(textContents)
        ? textContents
        : textContents?.textContent ?? [];
      const nestedTexts =
        textItems
          .map((textItem) => textItem.text?.trim())
          .filter((text): text is string => Boolean(text)) ?? [];
      const directText = typeof item.text === 'string' ? item.text.trim() : '';

      return [...nestedTexts, ...(directText ? [directText] : [])];
    }) ?? [];

  return textList.join('\n');
}

function resolveFixedReplyContent(node: Policy | SubPolicy) {
  return extractTextReplyContent(node);
}

function createBranchCardData(
  policy: Policy,
  node: Policy | SubPolicy,
  index: number,
  storeScope: BranchCardStoreScope,
  resolveTodoTypeLabel?: ResolveTodoTypeLabel,
  options?: BranchCardDisplayBase,
): BranchCardData {
  const policyId = options?.policyId ?? policy.policyId;
  const togglePolicyId = 'subPolicyId' in node ? node.subPolicyId : policy.policyId;
  const childPolicies = node.subPolicy ?? [];
  const triggerConditionSource = options?.triggerConditionSource ?? node.conditionRule;
  const resolvedReplyMode = resolveReplyMode(policy, node);
  const isFixedReply = resolvedReplyMode === 'FIXED_REPLY';
  const fixedReplyContent = resolveFixedReplyContent(node);
  const policyTriggerConditions = mapConditionGroups(
    policy.conditionRule?.conditionGroups,
    policy.conditionRule?.logicOperator,
  );

  return {
    policyId,
    togglePolicyId,
    index,
    title: options?.title ?? node.name ?? policy.name,
    enabled: options?.enabled ?? node.enable ?? policy.enable,
    triggerConditions: mapConditionGroups(
      triggerConditionSource?.conditionGroups,
      triggerConditionSource?.logicOperator,
    ),
    replyLogicDesc: resolveReplyLogicDesc(policy, node),
    replyExample: resolveReplyExample(policy, node),
    fixedReplyContent,
    receptionMode: childPolicies.length
      ? undefined
      : node.receptionMode ?? node.originalReceptionMode ?? 'AI_REPLY',
    originalReceptionMode: node.originalReceptionMode,
    todoType: node.todoType,
    todoLabel: resolveTodoTypeLabel?.(node.todoType) ?? node.todoType,
    isFixedReply,
    policyTriggerConditions,
    storeScope,
    headerVariant: options?.headerVariant,
    children: childPolicies.length
      ? childPolicies.map((subPolicy, childIndex) =>
          createBranchCardData(
            policy,
            subPolicy,
            childIndex + 1,
            storeScope,
            resolveTodoTypeLabel,
            {
              title: subPolicy.name ?? policy.name,
              enabled: subPolicy.enable ?? policy.enable,
              triggerConditionSource: subPolicy.conditionRule,
              headerVariant: 'sub',
              policyId: subPolicy.subPolicyId,
            },
          ),
        )
      : undefined,
  };
}

export function mapPolicies(
  scenario: Scenario,
  resolveTodoTypeLabel?: ResolveTodoTypeLabel,
): BranchCardData[] {
  const entries =
    scenario.policyGroup?.flatMap((policyGroup) =>
      policyGroup.policy.map((policy) => ({ policy, policyGroup })),
    ) ?? [];

  return entries.map(({ policy, policyGroup }, idx) => {
    const storeScope = createStoreScope(policyGroup);
    const subPolicies = policy.subPolicy ?? [];

    if (subPolicies.length <= 1) {
      return createBranchCardData(
        policy,
        subPolicies[0] ?? policy,
        idx + 1,
        storeScope,
        resolveTodoTypeLabel,
        {
          title: policy.name,
          enabled: policy.enable,
          triggerConditionSource: policy.conditionRule,
        },
      );
    }

    return createBranchCardData(policy, policy, idx + 1, storeScope, resolveTodoTypeLabel, {
      title: policy.name,
      enabled: policy.enable,
      triggerConditionSource: policy.conditionRule,
    });
  });
}

// ─── 根据分类 key 过滤场景 ───

export function filterScenariosByCategory(
  groups: IntentGroup[],
  categoryKey: string,
): ScenarioGroup[] {
  if (categoryKey === 'all') {
    return mapScenarioGroups(groups);
  }
  const group = groups.find((g) => g.intentGroupId === categoryKey);
  return group ? mapScenarioGroups([group]) : [];
}

export function filterScenarioGroupsByField(
  groups: ScenarioGroup[],
  searchField?: string,
  keyword?: string,
): ScenarioGroup[] {
  const normalizedKeyword = keyword?.trim();

  if (!normalizedKeyword) return groups;

  const field: (typeof ScenarioSearchField)[keyof typeof ScenarioSearchField] =
    searchField === ScenarioSearchField.UPDATE_TIME
      ? ScenarioSearchField.UPDATE_TIME
      : ScenarioSearchField.HEAT_VALUE;

  return groups.filter((group) => {
    if (field === ScenarioSearchField.UPDATE_TIME) {
      return String(group.updateTime ?? '')
        .toLowerCase()
        .includes(normalizedKeyword.toLowerCase());
    }

    return String(group.hotCount).includes(normalizedKeyword);
  });
}

export function getScenarioGroupTimeValue(updateTime?: string | number): number {
  if (updateTime === undefined || updateTime === null) return 0;

  if (typeof updateTime === 'number') return updateTime;

  const normalizedTime = updateTime.trim();
  if (!normalizedTime) return 0;

  const numericTime = Number(normalizedTime);
  if (!Number.isNaN(numericTime)) {
    return numericTime < 1e12 ? numericTime * 1000 : numericTime;
  }

  const parsedTime = normalizedTime.includes('T')
    ? normalizedTime
    : normalizedTime.replace(/-/g, '/');
  const timestamp = new Date(parsedTime).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

export function sortScenarioGroups(groups: ScenarioGroup[], sortField?: string): ScenarioGroup[] {
  const field: ScenarioSortFieldValue =
    sortField === ScenarioSortField.UPDATE_TIME
      ? ScenarioSortField.UPDATE_TIME
      : ScenarioSortField.HEAT_VALUE;

  return [...groups].sort((prev, next) => {
    if (field === ScenarioSortField.UPDATE_TIME) {
      return (
        getScenarioGroupTimeValue(next.updateTime) - getScenarioGroupTimeValue(prev.updateTime)
      );
    }

    return next.hotCount - prev.hotCount;
  });
}
